import RichText from "@/components/atoms/RichText/RichText";
import Modal from "@/components/molecules/Modal";
import StoryblokComponent from "@/lib/bloks/StoryblokComponent/StoryblokComponent";
import useModal from "@/lib/hooks/useModal";
import { usePatchOptions } from "@/lib/hooks/useSelection/cart/patch";
import { useEngravingPrice } from "@/lib/hooks/useSelection/cart/useEngravingPrice";
import { CustomizationsStoryblok } from "@/types/storyblok-blok-types";
import clsx from "clsx";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { A11y, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { PatchForm } from "../Patch/PatchForm";
import { TabBar } from "../TabBar/TabBar";
import styles from "./engraving.module.css";
import { EngravingForm } from "./EngravingForm";

interface ICustomisationModalProps {
  content: CustomizationsStoryblok;
  setEngraving?: (engraving: string) => void;
  setPatch?: (patch: string) => void;
}

export const CustomisationModal = ({
  setEngraving,
  setPatch,
  content,
}: ICustomisationModalProps) => {
  const { closeModal, isOpen } = useModal("customisation", () => {});
  const engravingPrice = useEngravingPrice();
  const params = useSearchParams();
  const activeTab = params.get("tab");
  const t = useTranslations();
  const { options: patchOptions } = usePatchOptions();

  const accountTabs = [
    {
      name: "engraving",
      label: "CUSTOM ENGRAVING",
      disabled: !Boolean(setEngraving),
    },
    { name: "patch", label: "LETTER PATCH", disabled: !Boolean(setPatch) },
  ];

  return (
    <Modal title={content.modalHeader} modal={"customisation"} always>
      <div className={styles.customisationModal}>
        <div className={styles.contained}>
          {isOpen && (
            <TabBar
              className={styles.tabBar}
              tabs={accountTabs.filter((t) => !Boolean(t.disabled))}
            />
          )}
        </div>

        {(() => {
          switch (activeTab) {
            case "patch":
              const { patchDescription, patchBlocks } = content;
              if (!patchOptions) return null;
              return (
                <>
                  <div className={clsx(styles.customization, styles.contained)}>
                    {patchDescription && (
                      <RichText>{patchDescription}</RichText>
                    )}
                    <PatchForm
                      handleAddPatch={(patch) => {
                        setPatch && setPatch(patch);
                        closeModal && closeModal();
                      }}
                      options={patchOptions!}
                    />
                  </div>
                  <div className={styles.divider} />
                  <div className={clsx(styles.blokContainer, styles.contained)}>
                    {patchBlocks?.map((blok) => (
                      <StoryblokComponent blok={blok} key={blok._uid} />
                    ))}
                  </div>
                  <div className={styles.modalGallery}>
                    <Swiper
                      spaceBetween={16}
                      slidesPerView={"auto"}
                      scrollbar={{ draggable: true }}
                      navigation
                      modules={[Navigation, A11y]}
                    >
                      {content?.patchGallery?.map((item) => (
                        <SwiperSlide className={styles.slide} key={item._uid}>
                          {item && <StoryblokComponent blok={item} />}
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>
                </>
              );
            case "engraving":
              const {
                engravingDescription,
                engravingBlocks,
                engravingGallery,
              } = content;

              return (
                <>
                  <div className={clsx(styles.customization, styles.contained)}>
                    {engravingDescription && (
                      <RichText>{engravingDescription}</RichText>
                    )}
                    <EngravingForm
                      buttonLabel={`${t("components.engraving.button.add", { price: engravingPrice })}`}
                      handleAddEngraving={({ engraving }) => {
                        setEngraving && setEngraving(engraving);
                        closeModal();
                      }}
                    />
                  </div>
                  <div className={styles.divider} />
                  <div className={clsx(styles.blokContainer, styles.contained)}>
                    {engravingBlocks?.map((blok) => (
                      <StoryblokComponent blok={blok} key={blok._uid} />
                    ))}
                  </div>
                  <div className={styles.modalGallery}>
                    <Swiper
                      spaceBetween={16}
                      slidesPerView={"auto"}
                      scrollbar={{ draggable: true }}
                      navigation
                      modules={[Navigation, A11y]}
                    >
                      {content?.engravingGallery?.map((item) => (
                        <SwiperSlide className={styles.slide} key={item._uid}>
                          {item && <StoryblokComponent blok={item} />}
                        </SwiperSlide>
                      ))}
                    </Swiper>
                  </div>
                </>
              );
            default:
              return <div></div>;
          }
        })()}
      </div>
    </Modal>
  );
};
